// types/deliveryPoints.ts

// Original API response types (snake_case)
interface GlsPointResponse {
  id: string;
  name: string;
  open_hours: string[][];
  country: string;
  city: string;
  postal_code: string;
  street: string;
  phone: string;
  email: string;
}

interface InPostPointResponse {
  href: string;
  name: string;
  type: string[];
  status: string;
  location: {
    longitude: number;
    latitude: number;
  };
  location_type: string | null;
  location_date: string | null;
  location_description: string;
  location_description_1: string;
  location_description_2: string;
  distance: number;
  opening_hours: string;
  address: {
    line1: string;
    line2: string;
  };
  address_details: {
    city: string;
    province: string;
    post_code: string;
    street: string;
    building_number: string;
    flat_number: string | null;
  };
  phone_number: string | null;
  payment_point_descr: string;
  functions: string[];
  partner_id: number;
  is_next: boolean;
  payment_available: boolean;
  payment_type: Record<string, string>;
  virtual: string;
  recommended_low_interest_box_machines_list: any;
  apm_doubled: any;
  location_247: boolean;
  operating_hours_extended: {
    customer: Record<string, Array<{ start: number; end: number }>>;
  };
  agency: string;
  image_url: string;
  easy_access_zone: boolean;
  air_index_level: any;
  physical_type_mapped: any;
  physical_type_description: any;
}

// Converted camelCase types
export interface GlsPoint {
  id: string;
  name: string;
  openHours: string[][];
  country: string;
  city: string;
  postalCode: string;
  street: string;
  phone: string;
  email: string;
}

export interface InPostPoint {
  href: string;
  name: string;
  type: string[];
  status: string;
  location: {
    longitude: number;
    latitude: number;
  };
  locationType: string | null;
  locationDate: string | null;
  locationDescription: string;
  locationDescription1: string;
  locationDescription2: string;
  distance: number;
  openingHours: string;
  address: {
    line1: string;
    line2: string;
  };
  addressDetails: {
    city: string;
    province: string;
    postCode: string;
    street: string;
    buildingNumber: string;
    flatNumber: string | null;
  };
  phoneNumber: string | null;
  paymentPointDescr: string;
  functions: string[];
  partnerId: number;
  isNext: boolean;
  paymentAvailable: boolean;
  paymentType: Record<string, string>;
  virtual: string;
  recommendedLowInterestBoxMachinesList: any;
  apmDoubled: any;
  location247: boolean;
  operatingHoursExtended: {
    customer: Record<string, Array<{ start: number; end: number }>>;
  };
  agency: string;
  imageUrl: string;
  easyAccessZone: boolean;
  airIndexLevel: any;
  physicalTypeMapped: any;
  physicalTypeDescription: any;
}

// Union type for selected delivery points
export type SelectedDeliveryPoint = GlsPoint | InPostPoint | null;

// Type guards
export const isGlsPoint = (point: SelectedDeliveryPoint): point is GlsPoint => {
  return point !== null && "openHours" in point;
};

export const isInPostPoint = (
  point: SelectedDeliveryPoint,
): point is InPostPoint => {
  return point !== null && "href" in point;
};

// Utility functions for conversion
export const convertToGlsPoint = (apiResponse: GlsPointResponse): GlsPoint => {
  return {
    id: apiResponse.id,
    name: apiResponse.name,
    openHours: apiResponse.open_hours,
    country: apiResponse.country,
    city: apiResponse.city,
    postalCode: apiResponse.postal_code,
    street: apiResponse.street,
    phone: apiResponse.phone,
    email: apiResponse.email,
  };
};

export const convertToInPostPoint = (
  apiResponse: InPostPointResponse,
): InPostPoint => {
  return {
    href: apiResponse.href,
    name: apiResponse.name,
    type: apiResponse.type,
    status: apiResponse.status,
    location: {
      longitude: apiResponse.location.longitude,
      latitude: apiResponse.location.latitude,
    },
    locationType: apiResponse.location_type,
    locationDate: apiResponse.location_date,
    locationDescription: apiResponse.location_description,
    locationDescription1: apiResponse.location_description_1,
    locationDescription2: apiResponse.location_description_2,
    distance: apiResponse.distance,
    openingHours: apiResponse.opening_hours,
    address: {
      line1: apiResponse.address.line1,
      line2: apiResponse.address.line2,
    },
    addressDetails: {
      city: apiResponse.address_details.city,
      province: apiResponse.address_details.province,
      postCode: apiResponse.address_details.post_code,
      street: apiResponse.address_details.street,
      buildingNumber: apiResponse.address_details.building_number,
      flatNumber: apiResponse.address_details.flat_number,
    },
    phoneNumber: apiResponse.phone_number,
    paymentPointDescr: apiResponse.payment_point_descr,
    functions: apiResponse.functions,
    partnerId: apiResponse.partner_id,
    isNext: apiResponse.is_next,
    paymentAvailable: apiResponse.payment_available,
    paymentType: apiResponse.payment_type,
    virtual: apiResponse.virtual,
    recommendedLowInterestBoxMachinesList:
      apiResponse.recommended_low_interest_box_machines_list,
    apmDoubled: apiResponse.apm_doubled,
    location247: apiResponse.location_247,
    operatingHoursExtended: {
      customer: apiResponse.operating_hours_extended.customer,
    },
    agency: apiResponse.agency,
    imageUrl: apiResponse.image_url,
    easyAccessZone: apiResponse.easy_access_zone,
    airIndexLevel: apiResponse.air_index_level,
    physicalTypeMapped: apiResponse.physical_type_mapped,
    physicalTypeDescription: apiResponse.physical_type_description,
  };
};
